import React, { useEffect, useState } from "react";
import { ISubTreatment } from "../types/clinic.types";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
interface Props {
  subTreatment: ISubTreatment | null; // null for 'Add', object for 'Edit'
  onClose: () => void;
  onSave: (subTreatment: ISubTreatment) => void;
}

const DEFAULT_SUB_TREATMENT: Omit<ISubTreatment, "id"> = {
  subTreatment: "",
  description: "",
  localPrice: 0,
  duration: 0,
  foreignerPrice: 0,
  minlocalPrice: 0,
  minforeignerPrice: 0,
  maxlocalPrice: 0,
  maxforeignerPrice: 0,
  note: "",
};

export const SubTreatmentFormModal: React.FC<Props> = ({
  subTreatment,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState(
    subTreatment || DEFAULT_SUB_TREATMENT
  );
  const isEditMode = !!subTreatment;

  useEffect(() => {
    setFormData(subTreatment || DEFAULT_SUB_TREATMENT);
  }, [subTreatment]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData as ISubTreatment);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000] p-4">
      <div
        className="bg-white rounded-[8px] shadow-2xl max-w-[500px] w-full p-3 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-slate-800 mb-0">
              {isEditMode ? "Edit Sub-Treatment" : "Add New Sub-Treatment"}
            </h3>
            <button
              type="button"
              className="p-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-full transition-colors"
              onClick={onClose}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Sub-Treatment Name</label>
              <input
                type="text"
                name="subTreatment"
                value={formData.subTreatment}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-y min-h-[80px] text-xs text-slate-700"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Duration (min)</label>
              <input
                type="number"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Local Price</label>
              <input
                type="number"
                name="localPrice"
                value={formData.localPrice}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Foreigner Price</label>
              <input
                type="number"
                name="foreignerPrice"
                value={formData.foreignerPrice}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Local Price min-max</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  name="minlocalPrice"
                  value={formData.minlocalPrice}
                  onChange={handleChange}
                  placeholder="min"
                  required
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
                />
                <input
                  type="number"
                  name="maxlocalPrice"
                  value={formData.maxlocalPrice}
                  onChange={handleChange}
                  placeholder="max"
                  required
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Foreigner Price min-max</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  name="minforeignerPrice"
                  value={formData.minforeignerPrice}
                  onChange={handleChange}
                  placeholder="min"
                  required
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
                />
                <input
                  type="number"
                  name="maxforeignerPrice"
                  value={formData.maxforeignerPrice}
                  onChange={handleChange}
                  placeholder="max"
                  required
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Note</label>
              <textarea
                name="note"
                value={formData.note === 0 || formData.note === undefined ? "" : String(formData.note)}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-y min-h-[80px] text-xs text-slate-700"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors text-xs" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-xs">
              <SaveAltIcon sx={{ fontSize: 16 }} />
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
